import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import {
  fetchSapAuthRequestById,
  fetchSapAuthMasterAll,
  approveRejectSapAuthRequest,
  ISapAuthorizationRequest,
  ISapAuthorizationTCode,
  ISapMasterOption,
} from '../../../core/data/redux/sapAuthorizationSlice';
import { getMasterText, getSapStatusBadge } from './SapAuthorizationList';
import { getRequestStatusConfig } from '../../../core/data/constants/requestStatus';

const SapAuthorizationDetails: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const employeesState = useSelector((state: RootState) => state.employeesState);
  const sapState = useSelector((state: RootState) => state.sapAuthorizationState);

  const profile = employeesState?.data?.data?.details || employeesState?.data?.details;
  const loggedInEmpId = String(profile?.empId || profile?.employee_id || '');

  const [modalAction, setModalAction] = useState<'A' | 'R' | null>(null);
  const [remarks, setRemarks] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const loadDetails = () => {
    if (id) {
      dispatch(fetchSapAuthRequestById({ ReferenceNumber: id }));
    }
  };

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(fetchSapAuthMasterAll({}));
    loadDetails();
  }, [dispatch, id]);

  const masterData = sapState.masterData || {};
  const plantsList: ISapMasterOption[] = masterData.Plants || [];
  const wingsList: ISapMasterOption[] = masterData.Wings || [];
  const businessAreasList: ISapMasterOption[] = masterData.Business_Areas || [];
  const purchaseOrgsList: ISapMasterOption[] = masterData.Purchase_Organizations || [];
  const authRevokedList: ISapMasterOption[] = masterData.Authorization_Revoked || [];
  const serversList: ISapMasterOption[] = masterData.SAP_servers || [];
  const modulesList: ISapMasterOption[] = masterData.SAP_Modules || [];

  const rawDetail = sapState.detailData;
  const reqData: ISapAuthorizationRequest = rawDetail && typeof rawDetail === 'object' ? rawDetail : {};

  const rawTcodes: any = reqData?.ToTcodes;
  const tcodesList: ISapAuthorizationTCode[] = rawTcodes && typeof rawTcodes === 'object' && Array.isArray(rawTcodes.results)
    ? rawTcodes.results
    : Array.isArray(rawTcodes)
      ? rawTcodes
      : Array.isArray((reqData as any)?.results)
        ? (reqData as any).results
        : [];

  const rawRemarks: any = reqData?.ToRemarks;
  const remarksTrail: any[] = rawRemarks && typeof rawRemarks === 'object' && Array.isArray(rawRemarks.results)
    ? rawRemarks.results
    : Array.isArray(rawRemarks)
      ? rawRemarks
      : [];

  const lastApproverEntry = [...remarksTrail]
    .reverse()
    .find((r: any) => r.ApproverName && String(r.ApproverName).trim() && r.Usertype === 'A') ||
    [...remarksTrail].reverse().find((r: any) => r.ApproverName && String(r.ApproverName).trim() && String(r.ApproverUserID).trim() !== String(reqData?.EmpID || '').trim());

  const displayApproverName =
    (reqData.approverName && String(reqData.approverName).trim()) ||
    (reqData.ApproverName && String(reqData.ApproverName).trim()) ||
    (reqData.LatestApproverName && String(reqData.LatestApproverName).trim()) ||
    (lastApproverEntry?.ApproverName && String(lastApproverEntry.ApproverName).trim()) ||
    '-';

  const displayApproverId =
    (reqData.ApproverUserID && String(reqData.ApproverUserID).trim()) ||
    (reqData.ApproverID && String(reqData.ApproverID).trim()) ||
    (lastApproverEntry?.ApproverUserID && String(lastApproverEntry.ApproverUserID).trim()) ||
    '-';

  const reqStatus = String(reqData?.RequestStatus || reqData?.requestStatus || '').trim().toUpperCase();
  const currentStatus = String(reqData?.Status || reqData?.status || reqData?.RequestStatus || '');
  const isDraft = Boolean(reqData?.draft || reqData?.is_draft);

  const isFromUniversalWorklist = Boolean(
    location.state?.isApprover ||
    location.state?.fromUniversalWorklist ||
    searchParams.get('isApprover') === 'true' ||
    searchParams.get('fromWorklist') === 'true'
  );

  const isUserApprover = Boolean(
    isFromUniversalWorklist ||
    (loggedInEmpId &&
      (String(reqData?.ApproverUserID || '').trim() === loggedInEmpId ||
        String(reqData?.ApproverName || '').trim() === loggedInEmpId) &&
      String(reqData?.EmpID || '').trim() !== loggedInEmpId)
  );

  const hasBeenActedOn =
    reqStatus === 'A' ||
    reqStatus === 'R' ||
    currentStatus.toLowerCase().includes('approve') ||
    currentStatus.toLowerCase().includes('reject');

  const isPending = !isDraft && !hasBeenActedOn;
  const canApproveReject = isPending && isUserApprover;

  const handleActionSubmit = async () => {
    if (!modalAction) return;
    if (modalAction === 'R' && !remarks.trim()) {
      toast.warning('Rejection remarks are mandatory. Please provide a reason.');
      return;
    }

    const refNo = String(reqData?.ReferenceNumber || reqData?.referenceNumber || id || '');
    const empId = String(reqData?.EmpID || reqData?.empId || '');

    setIsProcessing(true);

    const approverEmpId = loggedInEmpId || String(reqData?.ApproverUserID || reqData?.ApproverID || '00022088');

    const payload = {
      Action: modalAction,
      ReferenceNumber: refNo,
      EmpID: empId,
      ARemarks: remarks.trim(),
      Remarks: remarks.trim(),
      ApproverID: approverEmpId,
      ApproverUserID: approverEmpId,
    };

    try {
      const res = await dispatch(approveRejectSapAuthRequest(payload));
      if (res.meta.requestStatus === 'fulfilled') {
        toast.success(
          modalAction === 'A'
            ? 'SAP Authorization request approved successfully!'
            : 'SAP Authorization request rejected.'
        );
        setModalAction(null);
        setRemarks('');
        navigate(routes.todoWorklist?.tasks || '/todo-worklist/tasks');
      } else {
        toast.error((res.payload as string) || 'Failed to submit approval action');
      }
    } catch {
      toast.error('An error occurred while processing the approval action');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (val?: string) => {
    if (!val) return '-';
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    } catch { }
    return val;
  };

  const plantName = getMasterText(reqData.Plant, plantsList);
  const businessAreaName = getMasterText(reqData.BusinessArea, businessAreasList);
  const purchaseOrgName = getMasterText(reqData.PurchasingOrganization, purchaseOrgsList);
  const authRevokedName = getMasterText(reqData.AuthorizationRevoke, authRevokedList);
  const wingName = getMasterText(reqData.Wing, wingsList);
  const serverName = getMasterText(reqData.SAPServer, serversList);
  const moduleName = getMasterText(reqData.SAPModule, modulesList);

  const isLoading = sapState.loading === 'pending' && !rawDetail;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header Bar */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center gap-2 mb-1">
              <h3 className="page-title mb-0 text-dark fw-bold">
                <i className="ti ti-lock-access me-2 text-danger"></i>
                SAP ERP Authorization/ID Creation Request
              </h3>
              {getSapStatusBadge(reqData.RequestStatus || reqData.Status, isDraft)}
            </div>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.sapAuthorization.list}>SAP Authorization Forms</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Details (#{reqData.ReferenceNumber || id})
                </li>
              </ol>
            </nav>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Link to={routes.sapAuthorization.list} className="btn btn-outline-secondary d-flex align-items-center">
              <i className="ti ti-arrow-left me-1"></i>Back to List
            </Link>
            {isDraft && (
              <Link
                to={`/sap-authorization/edit/${reqData.ReferenceNumber || id}`}
                className="btn btn-warning d-flex align-items-center"
              >
                <i className="ti ti-edit me-1"></i>Edit Draft
              </Link>
            )}
            {canApproveReject && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-danger d-flex align-items-center px-3"
                  onClick={() => {
                    setModalAction('R');
                    setRemarks('');
                  }}
                >
                  <i className="ti ti-x me-1"></i>Reject
                </button>
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center px-4"
                  onClick={() => {
                    setModalAction('A');
                    setRemarks('');
                  }}
                >
                  <i className="ti ti-check me-1"></i>Approve
                </button>
              </>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-2 fs-13">Loading SAP Authorization details...</p>
          </div>
        ) : (
          <div className="row g-4">
            <div className="col-12">
              {/* Application Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-briefcase me-2 text-danger"></i>Application Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Reference Number</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.ReferenceNumber || id || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Approver ID</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={displayApproverId}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Approver Name</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={displayApproverName}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* User Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-user me-2 text-danger"></i>User Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">User Category</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.NewOrExistingUser === 'E' ? 'Existing SAP User' : 'New SAP User'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Employee No.</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.EmpID || reqData.empId || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Name</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.EmployeeName || reqData.EmpName || reqData.employeeName || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Designation</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.Designation || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Department</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.Department || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Phone Number</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.ContactNumber || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-4 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Wing</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.WingText || wingName || reqData.Wing || '-'}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Authorization Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-shield me-2 text-danger"></i>Authorization Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Purchasing Group</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.PurchaseGroup || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Plant</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.Plant || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Business Area</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.BusinessArea || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">Purchasing Organization</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.PurchasingOrganization || '-'}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">SAP Server</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={getMasterText(reqData.SAPServer, serversList) !== '-' ? getMasterText(reqData.SAPServer, serversList) : (reqData.SAPServer || '-')}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="col-md-6 col-sm-6">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">SAP module</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={getMasterText(reqData.SAPModule, modulesList) !== '-' ? getMasterText(reqData.SAPModule, modulesList) : (reqData.SAPModule || '-')}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Codes with Description Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-code me-2 text-danger"></i>Transaction Codes with Description
                  </h5>
                  <span className="badge bg-danger-subtle text-danger px-2 py-1">
                    {tcodesList.length} Items
                  </span>
                </div>
                <div className="card-body p-4">
                  {tcodesList.length === 0 ? (
                    <div className="text-center py-3 text-muted fs-13">
                      No transaction codes added yet
                    </div>
                  ) : (
                    <div className="row g-3">
                      {tcodesList.map((t, idx) => (
                        <div key={idx} className="col-md-6">
                          <div className="p-3 bg-white rounded-3 border">
                            <h6 className="fw-bold text-dark mb-2 fs-13">
                              Transaction Code {idx + 1}
                            </h6>
                            <div className="mb-1">
                              <span className="text-muted fs-12 me-1">Code:</span>
                              <span className="fw-bold text-danger fs-13 font-monospace">
                                {t.TransactionCode}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted fs-12 me-1">Description:</span>
                              <span className="text-dark fs-13">{t.Remarks || '-'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Revocation Details Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-refresh me-2 text-danger"></i>Revocation Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">
                    In case of requesting transactions of already existing user, whether authorizations to be revoked from existing user
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      reqData.AuthorizationRevoke === 'A'
                        ? 'Not Applicable'
                        : reqData.AuthorizationRevoke === 'Y'
                          ? 'Yes'
                          : reqData.AuthorizationRevoke === 'N'
                            ? 'No'
                            : authRevokedName || reqData.AuthorizationRevoke || 'Not Applicable'
                    }
                    readOnly
                    disabled
                  />
                  {reqData.AuthorizationRevoke === 'Y' && (
                    <div className="mt-3">
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">
                        Employee number of user whose authorizations are to be revoked
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={reqData.ExistingUserID || '-'}
                        readOnly
                        disabled
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Previous Remarks Card */}
              <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-messages me-2 text-danger"></i>Previous Remarks
                  </h5>
                </div>
                <div className="card-body p-4">
                  {remarksTrail.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {remarksTrail.map((r: any, idx: number) => (
                        <div key={idx} className="p-3 bg-light rounded-3 border">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="fw-bold text-dark fs-13">
                              • {r.ApproverName || 'Approver'} <span className="text-muted fs-11">+{r.ApproverUserID || '-'}</span>
                            </span>
                            <small className="text-muted fs-11">{r.TimeStamp || formatDate(r.created_at || r.CreatedAt)}</small>
                          </div>
                          <div className="p-2 bg-white rounded border text-dark fs-13">
                            {r.HRemarks || r.ARemarks || r.Remarks || '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : reqData.Remarks || reqData.remarks ? (
                    <div className="p-3 bg-light rounded-3 border">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold text-dark fs-13">
                          • {reqData.EmployeeName || reqData.EmpName || 'Applicant'} <span className="text-muted fs-11">+{reqData.EmpID || '-'}</span>
                        </span>
                        <small className="text-muted fs-11">{formatDate(reqData.RequestCreationDate || reqData.created_at)}</small>
                      </div>
                      <div className="p-2 bg-white rounded border text-dark fs-13">
                        {reqData.Remarks || reqData.remarks}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-3 text-muted fs-13">
                      No remarks recorded yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approve / Reject Modal */}
        {modalAction && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header border-bottom py-3 px-4">
                  <h5 className="modal-title fw-bold text-dark">
                    {modalAction === 'A' ? (
                      <>
                        <i className="ti ti-circle-check text-success me-2"></i>Approve SAP Authorization
                      </>
                    ) : (
                      <>
                        <i className="ti ti-circle-x text-danger me-2"></i>Reject SAP Authorization
                      </>
                    )}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setModalAction(null)}
                    disabled={isProcessing}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <p className="text-muted fs-13 mb-3">
                    You are taking action on SAP Authorization request{' '}
                    <strong className="text-dark">{reqData.ReferenceNumber || id}</strong> for employee{' '}
                    <strong className="text-dark">
                      {reqData.EmpName || reqData.EmployeeName || reqData.EmpID}
                    </strong>
                    .
                  </p>

                  <div className="mb-3">
                    <label className="form-label fs-13 fw-semibold text-dark mb-1">
                      {modalAction === 'A' ? 'Approval Remarks' : 'Rejection Reason'}{' '}
                      {modalAction === 'R' && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder={
                        modalAction === 'A'
                          ? 'Enter approval note or provisioning instructions...'
                          : 'Enter specific reason for rejecting this authorization request...'
                      }
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      autoFocus
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer border-top py-3 px-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setModalAction(null)}
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn px-4 ${modalAction === 'A' ? 'btn-success' : 'btn-danger'}`}
                    disabled={isProcessing}
                    onClick={handleActionSubmit}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : modalAction === 'A' ? (
                      'Confirm Approval'
                    ) : (
                      'Confirm Rejection'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SapAuthorizationDetails;
